import { Box, IconButton, InputAdornment, LinearProgress, TextField, Typography } from "@mui/material";
import { Close, Receipt, Search } from "@mui/icons-material";
import CustomDatePicker from "../../components/ui/customDatePicker/customDatePicker.component";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { SaleSummaryDetail } from "../contracts/viewContract/saleSummary/saleSummaryDetail.component";
import { useHasPermission } from "../../contexts/auth";
import { useBreadcrumbs } from "../../components/ui/breadcrumps/breadcrumbsContext";
import { StatCard } from "../dashboard/dashboard.component";
import { useAnalytics } from "../../api/analytics";
import { AnalyticsData } from "../../types/analytics";

export const SettlementsList = () => {
  const { t } = useTranslation();
  const canViewSaleSummary = useHasPermission("settlement.view_sale_summary");
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(dayjs());
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const { setOverride, clearOverride } = useBreadcrumbs();
  const { getAnalytics } = useAnalytics();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!selectedMonth) {
      setAnalyticsData(null);
      return;
    }

    const data = await getAnalytics({
      year: selectedMonth.year(),
      month: selectedMonth.month() + 1,
    });
    if (data) {
      setAnalyticsData(data);
    }
  }, [getAnalytics, selectedMonth]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    if (selectedMonth) {
      setOverride("/settlements", `${t("Settlements")} (${selectedMonth.format("YYYY-MM")})`);
    } else {
      clearOverride("/settlements");
    }
    return () => clearOverride("/settlements");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, t]);

  if (!canViewSaleSummary) {
    return (
      <Typography color="text.secondary" sx={{ p: 2, mt: 2 }}>
        {t("You do not have permission to view the sale summary.")}
      </Typography>
    );
  }

  const allCurrentMonth = analyticsData?.settlements.all_current_month || 0;
  const readyToSettleCurrentMonth = analyticsData?.settlements.ready_to_settle_current_month || 0;
  const confirmedCurrentMonth = analyticsData?.settlements.confirmed_current_month || 0;
  const readyToSettleProgress =
    allCurrentMonth > 0 ? (readyToSettleCurrentMonth / allCurrentMonth) * 100 : 0;
  const readyToSettleProgressClamped = Math.max(0, Math.min(100, readyToSettleProgress));
  const settledProgress =
    readyToSettleCurrentMonth > 0 ? (confirmedCurrentMonth / readyToSettleCurrentMonth) * 100 : 0;
  const settledProgressClamped = Math.max(0, Math.min(100, settledProgress));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        mt: 2,
        width: "100%",
        boxSizing: "border-box",
        alignSelf: "stretch",
      }}
    >
      {analyticsData && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
          <StatCard
            title={t("Ready to Settle / Selected Month")}
            value={(
              <Box sx={{ minWidth: 170 }}>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {`${readyToSettleCurrentMonth}/${allCurrentMonth}`}
                </Typography>
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={readyToSettleProgressClamped}
                    sx={{ flex: 1, height: 8, borderRadius: 999 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {`${Math.round(readyToSettleProgressClamped)}%`}
                  </Typography>
                </Box>
              </Box>
            )}
            icon={<Receipt />}
            color="#0288d1"
          />
          <StatCard
            title={t("Settled / Ready to Settle")}
            value={(
              <Box sx={{ minWidth: 170 }}>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {`${confirmedCurrentMonth}/${readyToSettleCurrentMonth}`}
                </Typography>
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={settledProgressClamped}
                    sx={{ flex: 1, height: 8, borderRadius: 999 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {`${Math.round(settledProgressClamped)}%`}
                  </Typography>
                </Box>
              </Box>
            )}
            icon={<Receipt />}
            color="#2e7d32"
          />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ flexShrink: 0, width: 200 }}>
          <CustomDatePicker
            label={t("Month")}
            value={selectedMonth}
            onChange={(value: Dayjs | null) => setSelectedMonth(value)}
            views={["year", "month"]}
            openTo="month"
            format="YYYY-MM"
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        </Box>
        <TextField
          label={t("Search by machine serial, client, contract number")}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          fullWidth
          sx={{ flex: 1, minWidth: 0, maxWidth: "100%" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                  }}
                  aria-label={t("Clear")}
                >
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>
      {selectedMonth && (
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <SaleSummaryDetail
            saleDate={selectedMonth.format("YYYY-MM")}
            search={search}
            showHeader={false}
          />
        </Box>
      )}
    </Box>
  );
};
